import { Editor, mergeAttributes, Node } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import { isEqual } from 'lodash-es';

interface ParagraphBreakFont {
    fontFamily: string;
    fontSize: string;
    backgroundColor: string | null;
    color: string | null;
    bold: boolean;
    italic: boolean;
    underline: boolean;
    strike: boolean;
    subscript: boolean;
    superscript: boolean;
}

export interface WordParagraphOptions {
    HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        wordParagraph: {
            /**
             * 선택한 노드를 paragraph로 변경합니다.
             */
            setParagraph: () => ReturnType;
            /**
             * paragraph 노드의 속성을 변경합니다.
             */
            setParagraphBreakFont: (paragraphBreakFont: ParagraphBreakFont) => ReturnType;
            /**
             * paragraph 노드의 속성을 적용합니다.
             */
            applyParagraphBreakFont: () => ReturnType;
            /**
             * 문단 전체가 선택된 경우 `paragraphBreakFont` 속성을 업데이트하는 함수
             * @param attrs 변경할 paragraphBreakFont 속성 (예: { bold: true })
             */
            syncParagraphBreakFontWithUpdatedMark: (markType: string) => ReturnType;
            syncParagraphBreakFontWithUpdatedTextStyle: (styleType: string) => ReturnType;
        };
    }
}

// 현재 커서 위치의 문단에서 `paragraphBreakFont` 속성을 가져오는 함수
export function getParagraphBreakFont(tr: Transaction): ParagraphBreakFont {
    const { selection, storedMarks } = tr;
    const { from } = selection;

    // 현재 커서 위치에서 `paragraph`를 찾기 위해 탐색
    let resolvedPos = tr.doc.resolve(from);
    let paragraphNode: ProseMirrorNode | null = null;
    let depth = resolvedPos.depth;

    while (depth >= 0) {
        const node = resolvedPos.node(depth);
        if (node.type.name === 'paragraph') {
            paragraphNode = node;
            break;
        }
        depth -= 1;
    }

    // `paragraphBreakFont` 속성 가져오기
    const paragraphBreakFont = paragraphNode?.attrs.paragraphBreakFont || {};

    return {
        fontFamily: paragraphBreakFont.fontFamily || null,
        fontSize: paragraphBreakFont.fontSize || '12pt',
        backgroundColor: paragraphBreakFont.backgroundColor || null,
        color: paragraphBreakFont.color || null,
        bold: !!paragraphBreakFont.bold,
        italic: !!paragraphBreakFont.italic,
        underline: !!paragraphBreakFont.underline,
        strike: !!paragraphBreakFont.strike,
        subscript: !!paragraphBreakFont.subscript,
        superscript: !!paragraphBreakFont.superscript,
    };
}

/**
 * 현재 커서 위치에서 `paragraphBreakFont` 속성을 가져오는 함수
 * @param editor Tiptap Editor 인스턴스
 * @returns paragraphBreakFont 속성 값
 */
export function getActiveParagraphBreakFont(editor: Editor): ParagraphBreakFont {
    return {
        fontFamily: editor.isActive('textStyle') ? editor.getAttributes('textStyle').fontFamily || null : null,
        fontSize: editor.isActive('textStyle') ? editor.getAttributes('textStyle').fontSize || '12pt' : '12pt',
        backgroundColor: editor.isActive('textStyle') ? editor.getAttributes('textStyle').backgroundColor || null : null,
        color: editor.isActive('textStyle') ? editor.getAttributes('textStyle').color || null : null,
        bold: editor.isActive('bold'),
        italic: editor.isActive('italic'),
        underline: editor.isActive('underline'),
        strike: editor.isActive('strike'),
        subscript: editor.isActive('subscript'),
        superscript: editor.isActive('superscript'),
    };
}

export const WordParagraph = Node.create<WordParagraphOptions>({
    name: 'paragraph',

    priority: 1000,

    addOptions() {
        return {
            HTMLAttributes: {},
        };
    },
    addAttributes() {
        return {
            paragraphBreakFont: {
                default: null,
                parseHTML: element => JSON.parse(element.dataset.paragraphBreakFont || 'null') || null,
                renderHTML: attributes => {
                    if (attributes.paragraphBreakFont === null) {
                        return {};
                    }
                    return {
                        'data-paragraph-break-font': JSON.stringify(attributes.paragraphBreakFont),
                    };
                },
            },
        };
    },
    // 처음 에디터가 생성되었을때 paragraphBreakFont가 세팅될 수 있도록 설정
    onFocus() {
        const { editor } = this;
        const storedMarks = editor.state.storedMarks;
        if (!storedMarks) {
            const { empty, $anchor, $from, $to } = editor.state.selection;
            // 셀렉션으로 선택한 영역이 없고 새로운 문단에 커서가 위치했을때 기본 textStyle mark 스타일 적용
            if (editor.isEditable && empty && $anchor.parent.type.name === this.name && $anchor.parentOffset === 0 && !$anchor.nodeAfter && !editor.isActive('textStyle')) {
                if ($anchor.parent.attrs.paragraphBreakFont) {
                    editor.commands.applyParagraphBreakFont();
                } else {
                    editor.commands.setFontSize('12pt');
                }
            }
        }
    },
    onSelectionUpdate() {
        const { editor } = this;
        const { empty, $anchor, $from, $to } = editor.state.selection;
        const storedMarks = editor.state.storedMarks;

        // 셀렉션으로 선택한 영역이 없고 새로운 문단에 커서가 위치했을때 기본 textStyle mark 스타일 적용
        if (editor.isEditable && empty && !storedMarks && $anchor.parent.type.name === this.name && $anchor.parentOffset === 0 && !$anchor.nodeAfter && !editor.isActive('textStyle')) {
            if ($anchor.parent.attrs.paragraphBreakFont) {
                editor.commands.applyParagraphBreakFont();
            } else {
                editor.commands.setFontSize('12pt');
            }
        }
    },

    group: 'block',

    content: 'inline*',

    parseHTML() {
        return [{ tag: 'p' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['p', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    },

    addCommands() {
        return {
            setParagraph:
                () =>
                ({ commands }) => {
                    return commands.setNode(this.name);
                },
            setParagraphBreakFont:
                paragraphBreakFont =>
                ({ commands }) => {
                    return commands.updateAttributes(this.name, { paragraphBreakFont });
                },
            applyParagraphBreakFont:
                () =>
                ({ tr, chain, ...rest }) => {
                    const { empty, $anchor, $from, $to } = tr.selection;
                    const paragraphNode = $anchor.node($anchor.depth);
                    if (paragraphNode.type.name === 'paragraph') {
                        const paragraphBreakFont = paragraphNode.attrs.paragraphBreakFont;
                        return chain()
                            .focus()
                            .setFontFamily(paragraphBreakFont?.fontFamily || 'Pretendard')
                            .setFontSize(paragraphBreakFont?.fontSize || '12pt')
                            .setBackgroundColor(paragraphBreakFont?.backgroundColor || null)
                            .setColor(paragraphBreakFont?.color || null)
                            [paragraphBreakFont?.bold ? 'setBold' : 'unsetBold']()
                            [paragraphBreakFont?.italic ? 'setItalic' : 'unsetItalic']()
                            [paragraphBreakFont?.underline ? 'setUnderline' : 'unsetUnderline']()
                            [paragraphBreakFont?.strike ? 'setStrike' : 'unsetStrike']()
                            [paragraphBreakFont?.subscript ? 'setSubscript' : 'unsetSubscript']()
                            [paragraphBreakFont?.superscript ? 'setSuperscript' : 'unsetSuperscript']()
                            .run();
                    }
                    return true;
                },
            syncParagraphBreakFontWithUpdatedMark:
                (markType: string) =>
                ({ tr, dispatch }) => {
                    let hasChanged = false;
                    const { from, to } = tr.selection;

                    tr.doc.nodesBetween(from, to, (node, pos) => {
                        if (node.type.name === 'paragraph') {
                            const nodeStart = pos + 1;
                            const nodeEnd = pos + node.nodeSize - 1;

                            // 문단 전체가 선택되었는지 확인
                            const isFullySelected = from <= nodeStart && to >= nodeEnd;
                            if (!isFullySelected || nodeEnd <= nodeStart) return;

                            const marks = tr.doc.resolve(nodeStart).marks();
                            const isMarkActive = marks.some(mark => mark.type.name === markType);

                            const prevParagraphBreakFont = node.attrs.paragraphBreakFont || {};
                            const updatedParagraphBreakFont = {
                                ...prevParagraphBreakFont,
                                [markType]: isMarkActive,
                            };

                            // 변경 사항이 있는 경우만 업데이트
                            if (!isEqual(prevParagraphBreakFont, updatedParagraphBreakFont)) {
                                tr.setNodeMarkup(pos, undefined, {
                                    ...node.attrs,
                                    paragraphBreakFont: updatedParagraphBreakFont,
                                });
                                hasChanged = true;
                            }
                        }
                    });

                    if (hasChanged && dispatch) {
                        dispatch(tr);
                    }

                    return true;
                },
            syncParagraphBreakFontWithUpdatedTextStyle:
                (styleType: string) =>
                ({ tr, dispatch }) => {
                    let hasChanged = false;
                    const { from, to } = tr.selection;

                    tr.doc.nodesBetween(from, to, (node, pos) => {
                        if (node.type.name === 'paragraph') {
                            const nodeStart = pos + 1;
                            const nodeEnd = pos + node.nodeSize - 1;

                            // 문단 전체가 선택되었는지 확인
                            const isFullySelected = from <= nodeStart && to >= nodeEnd;
                            if (!isFullySelected || nodeEnd <= nodeStart) return;

                            const marks = tr.doc.resolve(nodeStart).marks();
                            const styleValue = marks.find(mark => mark.type.name === 'textStyle')?.attrs[styleType] || null;

                            const prevParagraphBreakFont = node.attrs.paragraphBreakFont || {};
                            const updatedParagraphBreakFont = {
                                ...prevParagraphBreakFont,
                                [styleType]: styleValue,
                            };

                            // 변경 사항이 있는 경우만 업데이트
                            if (!isEqual(prevParagraphBreakFont, updatedParagraphBreakFont)) {
                                tr.setNodeMarkup(pos, undefined, {
                                    ...node.attrs,
                                    paragraphBreakFont: updatedParagraphBreakFont,
                                });
                                hasChanged = true;
                            }
                        }
                    });

                    if (hasChanged && dispatch) {
                        dispatch(tr);
                    }

                    return true;
                },
        };
    },

    addKeyboardShortcuts() {
        return {
            Tab: ({ editor }) => {
                const { state } = editor;
                const { selection } = state;
                const { $from } = selection;

                const isParagraph = editor.isActive('paragraph');
                const isNumberingList = editor.isActive('numberingList');
                const isHtmlListItem = editor.can().liftListItem('listItem');
                const isAtStartOfParagraph = $from.parentOffset === 0;

                if (isParagraph && !(isNumberingList && isAtStartOfParagraph) && !(isHtmlListItem && isAtStartOfParagraph)) {
                    editor.chain().insertContent('\t').run();
                    return true; // Prevent default Tab behavior
                }

                return false;
            },
        };
    },
});
